/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable, OnDestroy } from '@angular/core';
import { filter, interval, Subscription } from 'rxjs';
import { ObservabilityProxyStompService } from './observability-proxy-stomp.service';
import { EnvironmentService } from './environment.service';
import { UiLogLevel } from '../../configuration/model/ui-log-streaming-config';

const LOG_STREAMING_INTERVAL_MS = 1000;

@Injectable({ providedIn: 'root' })
export class LogStreamingService implements OnDestroy {
    private streamingSubscription: Subscription | null = null;
    private logLevel: UiLogLevel = UiLogLevel.OFF;

    constructor(
        private readonly observabilityProxyStompService: ObservabilityProxyStompService,
        private readonly env: EnvironmentService
    ) {}

    private isFeatureEnabled(): boolean {
        return this.env.environment?.featureFlags?.uiLogStreaming === true;
    }

    private shouldStream(level: string): boolean {
        if (this.logLevel === UiLogLevel.OFF) {
            return false;
        }

        const order = {
            [UiLogLevel.TRACE]: 0,
            [UiLogLevel.DEBUG]: 1,
            [UiLogLevel.INFO]: 2,
            [UiLogLevel.WARN]: 3,
            [UiLogLevel.ERROR]: 4,
            [UiLogLevel.OFF]: 99
        };

        const normalizedLevel = (level === 'LOG' ? UiLogLevel.INFO : level).toUpperCase();
        const selectedLevel = this.logLevel.toUpperCase();
        return (order[normalizedLevel as UiLogLevel] ?? order[UiLogLevel.INFO]) >= (order[selectedLevel as UiLogLevel] ?? order[UiLogLevel.INFO]);
    }

    enable(level: UiLogLevel): void {
        if (!this.isFeatureEnabled() || level === UiLogLevel.OFF) {
            this.disable();
            return;
        }
        this.logLevel = level;
        (globalThis as any).__logStreamingFilter = (value: string) => this.shouldStream(value);
        this.observabilityProxyStompService.enable();
        this.startLogStreaming();
    }

    disable(): void {
        this.logLevel = UiLogLevel.OFF;
        (globalThis as any).__logStreamingFilter = () => false;
        if ((globalThis as any).__logStreamingQueue) {
            (globalThis as any).__logStreamingQueue.length = 0;
        }
        this.streamingSubscription?.unsubscribe();
        this.streamingSubscription = null;
        this.observabilityProxyStompService.disable();
    }

    ngOnDestroy(): void {
        this.disable();
    }

    private startLogStreaming(): void {
        if (this.streamingSubscription) {
            return;
        }
        this.streamingSubscription = interval(LOG_STREAMING_INTERVAL_MS)
            .pipe(filter(() => this.observabilityProxyStompService.connected()))
            .subscribe(() => this.drainStreamingQueue());
    }

    private drainStreamingQueue(): void {
        const queue = (globalThis as unknown as { __logStreamingQueue?: string[] }).__logStreamingQueue;
        if (!queue || queue.length === 0) {
            return;
        }

        const lines = queue.splice(0, queue.length);
        for (let i = 0; i < lines.length; i++) {
            try {
                this.observabilityProxyStompService.publishLog(lines[i]);
            } catch (error) {
                queue.unshift(...lines.slice(i));
                console.warn('[LogStreamingService] Publish failed; remaining log lines will be sent on next tick', error);
                return;
            }
        }
    }
}
